/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import { Grid } from '@mui/material'
import { BookingSummary } from 'components/Bookings/BookingSummary/BookingSummary'
import Form from 'components/RouteWizard/Form'
import { StepComponentProps } from 'components/RouteWizard/RouteWizard'
import { useWizard } from 'components/RouteWizard/Wizard'
import useBooking from 'hooks/useBooking'
import useClient from 'hooks/useClient'
import useClients from 'hooks/useClients'
import useClientUsers from 'hooks/useClientUsers'
import useFormState from 'hooks/useFormState'
import useQuote from 'hooks/useQuote'
import useUserRole from 'hooks/useUserRole'
import { fireBookingCreated } from 'analytics/bookingEvents'
import { useEffect, useRef, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useSelector } from 'react-redux'
import schema from '../AccountInformation/schema'
import AccountReference from './AccountReference'

export default function QQAccount({
  initialValues,
  onSuccess,
}: StepComponentProps) {
  const { data, addData } = useWizard()
  const formMethods = useRef<UseFormReturn | null>(null)
  const { clients, options: clientOptions } = useClients()
  const currentClient = useSelector((state: any) => state.clients.client)
  const [onlyTraveller] = useState<boolean>(
    useUserRole().currentUserRoles.length === 1 &&
      useUserRole().currentUserRoles[0] === 'TRAVELLER',
  )
  const {
    bookingData,
    createBooking,
    isLoading: isLoadingBooking,
  } = useBooking()

  const { prices } = useQuote()

  const [numPassengers, setNumPassengers] = useState(
    data?.account?.numberOfPassengers || 1,
  )

  const { users } = useClientUsers(data?.account?.clientId)
  const [showToggle, setShowToggle] = useState<boolean>(users)

  const [clientType, setClientType] = useState<'my' | 'other'>(
    data?.account?.accountType
      ? data?.account?.accountType
      : onlyTraveller
      ? 'my'
      : clients?.length > 1
      ? 'other'
      : 'my',
  )

  const [preview, setPreview] = useState<any>({})

  const [onBehalfOfToggle, setOnBehalfOfToggle] = useState<boolean>(
    data.account?.onBehalfOf ? true : false,
  )

  const mainPassenger = useFormState('mainPassenger', formMethods.current)
  const additionalPassengers = useFormState(
    'passengers',
    formMethods.current,
    true,
  )

  const onBehalfOfToggleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOnBehalfOfToggle(event.target.checked)
    if (!event.target.checked) {
      formMethods.current?.resetField('onBehalfOf', { defaultValue: '' })
    }
  }

  const { fixedPriceDisplay } = useClient(data?.account?.clientId)

  const onSubmit = async (fieldData: any) => {
    const passengers = [mainPassenger, ...(fieldData?.passengers || [])]
    const bookingReferences =
      fieldData?.bookingReferenceValues
        ?.filter((ref: any) => {
          if (!ref || ref.bookingReferenceId === undefined) return false
          const value = ref.value
          if (value === undefined || value === null) return false
          return String(value).trim().length > 0
        })
        .map((ref: any) => ({
          bookingReferenceId: ref.bookingReferenceId,
          value: String(ref.value).trim(),
        })) || []

    const prevAccountData = JSON.parse(JSON.stringify(data?.account || {}))

    prevAccountData.accountType = clientType
    prevAccountData.clientId =
      clientType === 'my' ? currentClient.id : fieldData.clientId
    prevAccountData.mainPassenger = fieldData?.mainPassenger
    prevAccountData.passengers = fieldData?.passengers || []
    prevAccountData.bookingReferenceValues = fieldData?.bookingReferenceValues
    prevAccountData.numberOfPassengers = numPassengers
    prevAccountData.numPassengers = numPassengers
    prevAccountData.fixedPrice = fixedPriceDisplay

    await addData({
      account: prevAccountData,
      preview: {
        ...preview,
        passengers,
      },
    })

    await createBooking({
      ...data?.transport,
      passengers,
      numberOfPassengers: passengers.length,
      ...(bookingReferences.length > 0 ? { bookingReferences } : {}),
    })
  }

  useEffect(() => {
    setPreview(data?.preview)
  }, [data?.preview])

  useEffect(() => {
    const hasLeadPassenger =
      mainPassenger && Object.keys(mainPassenger).length > 0
    const filteredAdditional =
      additionalPassengers?.filter(
        (passenger: any) => passenger && Object.keys(passenger).length,
      ) ?? []

    setPreview((prev: any) => ({
      ...prev,
      passengers: [
        ...(hasLeadPassenger ? [mainPassenger] : []),
        ...filteredAdditional,
      ],
    }))
  }, [mainPassenger, additionalPassengers])

  const bookingCreatedFiredRef = useRef<string | null>(null)
  useEffect(() => {
    if (bookingData) {
      const bookingId = (bookingData as any)?.id
      if (bookingId && bookingCreatedFiredRef.current !== String(bookingId)) {
        bookingCreatedFiredRef.current = String(bookingId)
        const toNum = (v: any) =>
          typeof v === 'number'
            ? Number(v.toFixed(2))
            : typeof v === 'string' && v.length
            ? Number(parseFloat(v).toFixed(2))
            : undefined
        const full = (preview as any)?.estimatedPriceFull
        fireBookingCreated({
          surface:
            data?.preview?.applyAutomaticAllocation === false
              ? 'quote_old'
              : 'quote_new',
          bookingId,
          bookingReference: (bookingData as any)?.bookingReference,
          vehicleType: (preview as any)?.selectedVehicle,
          winningPrice: toNum(full?.total ?? full),
          winningPriceExVat: toNum(full?.price),
          currency: full?.currency ?? (preview as any)?.estimatedPriceCurrency,
          journeyType: data?.plan?.journeyTypeName,
          bookingType: data?.type,
        })
      }
      onSuccess({
        transport: bookingData,
        prices: prices,
        preview,
      })
    }
  }, [bookingData])

  return (
    <Grid container spacing={2}>
      <Grid item xs={7}>
        <Form
          schema={schema}
          ref={formMethods}
          onSubmit={onSubmit}
          defaultValues={{
            ...data?.account,
            clientId: data?.account?.clientId || '0',
          }}
          showControls={false}
          formId={'accountInformation'}
          sx={{ float: 'right', marginRight: '40px' }}
        >
          <AccountReference
            onlyTraveller={onlyTraveller}
            clientId={data?.account?.clientId}
            selectedClientId={data?.account?.clientId}
            numPassengers={numPassengers}
            setNumPassengers={setNumPassengers}
          />
        </Form>
      </Grid>
      <Grid item xs={5}>
        <BookingSummary
          bookingPreview={preview}
          formId={'accountInformation'}
          isLoadingBooking={isLoadingBooking}
        />
      </Grid>
    </Grid>
  )
}
